import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import {
  AutoCompleteExtraProps,
  EditableTableFieldProps,
  FormFieldDefinitionType,
  FormHandles,
} from "models/propTypes/genericFormProps";
import { CurrencyEnums, FormEnums } from "models/enums";
import { SettingsFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { EditSalaryFormProps } from "models/propTypes/settingsPropTypes";
//#endregion
//#region components
import { GenericForm } from "components/common";
import { EmployeeProfileLink } from "components/common/profile";
import useSalaryPermissions from "../hooks/useSalaryPermissions";
//#endregion
import { EnumHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const EditSalariesForm: ForwardRefRenderFunction<FormHandles, EditSalaryFormProps> = (props, ref) => {
  const { salaryChangeHandler, salaryToggleHandler } = props;
  const { isWintSalaryDeviationsAvailable } = useSalaryPermissions();

  return (
    <GenericForm<SettingsFormTypes.SalarySettings>
      formRef={ref}
      {...props}
      fields={{
        salarySettings: {
          label: "",
          type: FormEnums.FormFieldType.EDITABLETABLE,
          innerFields: {
            id: {
              controlLabel: TranslationKeys.Common.Name,
              type: FormEnums.FormFieldType.CUSTOM,
              readonly: true,
              customField: <EmployeeProfileLink />,
            },
            monthlySalary: {
              controlLabel: TranslationKeys.Settings.StandardSalary,
              type: FormEnums.FormFieldType.CURRENCY,
              extraProps: {
                currency: CurrencyEnums.CurrencyCode.SEK,
              },
              onChangeHandler: salaryChangeHandler,
            },
            employmentRate: {
              controlLabel: TranslationKeys.Settings.EmploymentRate,
              type: FormEnums.FormFieldType.NUMBER,
              onChangeHandler: salaryChangeHandler,
              extraProps: {
                suffix: "%",
              },
            },
            taxTableName: {
              controlLabel: TranslationKeys.Settings.TaxTable,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              extraProps: {
                referenceFieldName: FormFieldConstants.Settings.EmployeeSettings.TaxTable,
                items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.TaxTableInfo).map((item) => ({
                  id: item,
                  value:
                    // eslint-disable-next-line eqeqeq --- since checked against different types
                    item == WintSuperkollAPIModel.TaxTableInfo.None
                      ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.None)
                      : item,
                })),
              } as AutoCompleteExtraProps<SettingsFormTypes.SalarySetting>,
              classes: {
                input: cssExports.taxTableField,
              },
            },
            employmentStartDate: {
              controlLabel: TranslationKeys.Common.StartDate,
              type: FormEnums.FormFieldType.DATE,
            },
            employmentEndDate: {
              controlLabel: TranslationKeys.Common.EndDate,
              type: FormEnums.FormFieldType.DATE,
            },
            ...(isWintSalaryDeviationsAvailable
              ? {}
              : {
                  salaryActive: {
                    controlLabel: TranslationKeys.Common.Active,
                    type: FormEnums.FormFieldType.TOGGLE,
                    extraProps: {
                      customLabels: {
                        active: "",
                        inactive: "",
                      },
                    },
                    onChangeHandler: salaryToggleHandler,
                  },
                }),
            workplaceStreetAddress: {
              controlLabel: TranslationKeys.Settings.WorkPlaceStreetAddress,
              type: FormEnums.FormFieldType.TEXT,
            },
            workplaceCity: {
              controlLabel: TranslationKeys.Settings.WorkPlaceCity,
              type: FormEnums.FormFieldType.TEXT,
            },
            salaryChangeFrom: {
              controlLabel: TranslationKeys.Settings.ApplyFrom,
              type: FormEnums.FormFieldType.DATE,
              disabled: (values: SettingsFormTypes.SalarySetting) => !values.salaryChanged,
              placeholder: " ",
            },
          } as FormFieldDefinitionType<SettingsFormTypes.SalarySetting>,
          extraProps: {
            idFieldKey: FormFieldConstants.Settings.SalarySetting.Id,
            hideActions: true,
            isCompactTable: true,
            showColumnHeaders: true,
            rowsPerPage: 10,
          } as EditableTableFieldProps<SettingsFormTypes.SalarySetting>,
          className: cssExports.asSimpleTable,
        },
      }}
    />
  );
};

export default forwardRef(EditSalariesForm);
